// AI Agent effectiveness scorecard — DAILY grouping.
//
// Same shape as BotEffectiveness but rows = (bot × day) instead of just bot.
// Supervisors want to see day-over-day drift ("did PatientType containment
// drop after the Thursday prompt change?") without re-fetching per day.
//
// Shares the same lib/sessionClassification.js classifier → the same 6-way
// outcome definitions + role inference, just bucketed per day.

import React, { useMemo, useState } from 'react';
import { Badge, Group, Progress, Text, Tooltip } from '@mantine/core';
import { ReportTable } from './ReportTable';
import { fmtDuration } from '../utils';
import { SessionListModal } from '../components/SessionListModal';
import {
  CLASSIFICATIONS,
  CLASSIFICATION_META,
  ROLE_META,
  ROLES,
  MIN_SESSIONS_FOR_ROLE,
  classifySessions,
  rollupByBot,
} from '../lib/sessionClassification';

const REPORT_ID = 'bot-effectiveness-by-day';

const COLUMNS = [
  {
    id: 'day',
    label: 'Day',
    pinned: true,
    render: (r) => <Text size="sm" fw={500}>{r.day}</Text>,
  },
  {
    id: 'bot',
    label: 'Bot',
    pinned: true,
    render: (r) => (
      <div>
        <Group gap={6} align="baseline">
          <Text size="sm">{r.name || <em>(unnamed)</em>}</Text>
          <Text size="xs" c="dimmed">
            (n={r.sessions}{r.resolvedTotal !== r.sessions ? `, resolved=${r.resolvedTotal}` : ''})
          </Text>
        </Group>
        {r.types.length > 0 && <Text size="xs" c="dimmed">{r.types.join(' / ')}</Text>}
      </div>
    ),
  },
  {
    id: 'role',
    label: 'Role (day)',
    hint: `Role inferred from this bot's behaviour on this specific day. Needs ≥${MIN_SESSIONS_FOR_ROLE} resolved sessions; otherwise Unknown. A bot can show different roles on different days if its behaviour drifts.`,
    render: (r) => <RoleBadge role={r.role} confidence={r.roleConfidence} resolvedTotal={r.resolvedTotal} />,
  },
  {
    id: 'sessions',
    label: 'Sessions',
    align: 'right',
    render: (r) => <Text size="sm">{r.sessions}</Text>,
  },
  {
    id: 'uniqueCallers',
    label: 'Unique callers',
    align: 'right',
    render: (r) => <Text size="sm">{r.uniqueCallers}</Text>,
  },
  {
    id: 'successRate',
    label: 'Success',
    hint: 'Routed-to-sub-bot + resolved-with-tools / total.',
    align: 'right',
    render: (r) => <RateBadge pct={r.successRate} color="teal" />,
  },
  {
    id: 'routedToAi',
    label: 'Routed → sub-bot',
    align: 'right',
    render: (r) => <RateBadge pct={r.pcts.routedToAi} color="cyan" />,
  },
  {
    id: 'routedToHuman',
    label: 'Routed → human',
    align: 'right',
    render: (r) => <RateBadge pct={r.pcts.routedToHuman} color="blue" />,
  },
  {
    id: 'resolvedWithTools',
    label: 'Resolved w/ tools',
    align: 'right',
    render: (r) => <RateBadge pct={r.pcts.resolvedWithTools} color="teal" />,
  },
  {
    id: 'resolvedNoTools',
    label: 'Resolved no tools',
    align: 'right',
    // Role-aware color (see BotEffectiveness.jsx for the full rationale).
    render: (r) => <RateBadge pct={r.pcts.resolvedNoTools} color={r.role === ROLES.TASK ? 'yellow' : 'gray'} />,
  },
  {
    id: 'abandonment',
    label: 'Abandonment',
    align: 'right',
    render: (r) => <RateBadge pct={r.pcts.abandoned} color="orange" />,
  },
  {
    id: 'failRate',
    label: 'Failed',
    align: 'right',
    render: (r) => <RateBadge pct={r.pcts.failed} color="red" />,
  },
  {
    id: 'evalScore',
    label: 'Avg eval',
    align: 'right',
    render: (r) => <EvalBadge score={r.avgEvalScore} />,
  },
  {
    id: 'avgMessages',
    label: 'Avg msgs',
    align: 'right',
    render: (r) => <Text size="sm">{r.avgMessages?.toFixed(1) ?? '—'}</Text>,
  },
  {
    id: 'avgResponseTime',
    label: 'Avg response',
    align: 'right',
    render: (r) => <Text size="sm">{r.avgResponseMs != null ? `${(r.avgResponseMs / 1000).toFixed(1)}s` : '—'}</Text>,
  },
  {
    id: 'avgDuration',
    label: 'Avg duration',
    align: 'right',
    render: (r) => <Text size="sm">{fmtDuration(r.avgDurationMs)}</Text>,
  },
  {
    id: 'mix',
    label: 'Outcome mix',
    width: 220,
    render: (r) => (
      <Tooltip
        label={
          `${r.pcts.routedToAi}% → sub-bot · ${r.pcts.resolvedWithTools}% resolved w/ tools · ` +
          `${r.pcts.resolvedNoTools}% resolved no tools · ${r.pcts.routedToHuman}% → human · ` +
          `${r.pcts.abandoned}% abandoned · ${r.pcts.failed}% failed`
        }
      >
        <Progress.Root size="md">
          {r.pcts.routedToAi        > 0 && <Progress.Section value={r.pcts.routedToAi}        color={CLASSIFICATION_META[CLASSIFICATIONS.ROUTED_TO_AI].color} />}
          {r.pcts.resolvedWithTools > 0 && <Progress.Section value={r.pcts.resolvedWithTools} color={CLASSIFICATION_META[CLASSIFICATIONS.RESOLVED_WITH_TOOLS].color} />}
          {r.pcts.resolvedNoTools   > 0 && <Progress.Section value={r.pcts.resolvedNoTools}   color={CLASSIFICATION_META[CLASSIFICATIONS.RESOLVED_NO_TOOLS].color} />}
          {r.pcts.routedToHuman     > 0 && <Progress.Section value={r.pcts.routedToHuman}     color={CLASSIFICATION_META[CLASSIFICATIONS.ROUTED_TO_HUMAN].color} />}
          {r.pcts.abandoned         > 0 && <Progress.Section value={r.pcts.abandoned}         color={CLASSIFICATION_META[CLASSIFICATIONS.ABANDONED].color} />}
          {r.pcts.failed            > 0 && <Progress.Section value={r.pcts.failed}            color={CLASSIFICATION_META[CLASSIFICATIONS.FAILED].color} />}
        </Progress.Root>
      </Tooltip>
    ),
  },
];

const DEFAULT_VISIBLE = [
  'sessions', 'uniqueCallers', 'successRate',
  'routedToAi', 'routedToHuman', 'resolvedWithTools', 'resolvedNoTools',
  'evalScore', 'avgResponseTime', 'mix',
];

// `analysis` (optional) — pre-computed { perBotDay, ... } from Analytics.jsx
// so we don't re-classify + re-sibling-join on every re-render. Falls back
// to inline compute if not provided.
//
// `onViewSession` (optional) — enables row-click drill into a SessionListModal
// showing all sessions for that (bot, day) bucket.
export function BotEffectivenessByDay({ data, analysis, onViewSession, window }) {
  const sessions = data?.sessions ?? [];
  const tasks = data?.tasks ?? [];
  const rows = useMemo(() => buildRows(sessions, tasks, analysis?.perBotDay), [sessions, tasks, analysis?.perBotDay]);
  const sessionsByKey = useMemo(() => groupSessionsByBotDay(sessions), [sessions]);
  const dayCount = new Set(rows.map((r) => r.day)).size;
  const botCount = new Set(rows.map((r) => r.name)).size;

  const [drill, setDrill] = useState(null); // { bot, day }

  const csvRows = useMemo(() => rows.map(toCsvRow), [rows]);
  const csvFilename = `bot-effectiveness-by-day-${new Date().toISOString().slice(0, 10)}.csv`;

  return (
    <>
      <ReportTable
        title="Bot effectiveness — by day"
        subtitle={`${rows.length} bot-day row${rows.length === 1 ? '' : 's'} · ${botCount} bot${botCount === 1 ? '' : 's'} × ${dayCount} day${dayCount === 1 ? '' : 's'}${onViewSession ? ' · click a row for sessions on that bot/day' : ''}`}
        reportId={REPORT_ID}
        columns={COLUMNS}
        defaultVisibleIds={DEFAULT_VISIBLE}
        rows={rows}
        emptyLabel="No AI Agent sessions recorded in this window."
        onRowClick={onViewSession ? (r) => setDrill({ bot: r.name, day: r.day }) : undefined}
        csvExport={{ filename: csvFilename, rows: csvRows }}
      />
      <SessionListModal
        opened={!!drill}
        onClose={() => setDrill(null)}
        title={drill ? `${drill.bot} · ${drill.day}` : ''}
        subtitle={drill ? `All sessions on this bot-day bucket` : ''}
        sessions={drill ? (sessionsByKey.get(`${drill.day}|${drill.bot}`) ?? []) : []}
        onViewSession={onViewSession}
        window={window}
      />
    </>
  );
}

function groupSessionsByBotDay(sessions) {
  const m = new Map();
  for (const s of sessions ?? []) {
    const name = s?.agentName ?? '(unnamed)';
    const day = isoDay(s?.sessionStartTime);
    if (!day) continue;
    const key = `${day}|${name}`;
    if (!m.has(key)) m.set(key, []);
    m.get(key).push(s);
  }
  return m;
}

function toCsvRow(r) {
  return {
    day: r.day,
    bot: r.name,
    role: r.role,
    role_confidence: r.roleConfidence != null ? r.roleConfidence.toFixed(2) : '',
    sessions: r.sessions,
    resolved_sessions: r.resolvedTotal,
    unique_callers: r.uniqueCallers,
    success_pct: r.successRate,
    routed_to_ai_pct: r.pcts.routedToAi,
    routed_to_human_pct: r.pcts.routedToHuman,
    resolved_with_tools_pct: r.pcts.resolvedWithTools,
    resolved_no_tools_pct: r.pcts.resolvedNoTools,
    abandoned_pct: r.pcts.abandoned,
    failed_pct: r.pcts.failed,
    avg_eval_score: r.avgEvalScore != null ? r.avgEvalScore.toFixed(2) : '',
    avg_messages: r.avgMessages != null ? r.avgMessages.toFixed(1) : '',
    avg_response_ms: r.avgResponseMs ?? '',
    avg_duration_ms: r.avgDurationMs ?? '',
  };
}

function buildRows(sessions, tasks, perBotDayOverride) {
  const rolled = perBotDayOverride ?? rollupByBot(classifySessions(sessions, tasks), { byDay: true });
  const extras = buildExtrasByDay(sessions);
  const extrasByKey = new Map(extras.map((e) => [`${e.day}|${e.name}`, e]));

  return rolled
    .map((r) => {
      const extra = extrasByKey.get(r.key) ?? {};
      const pcts = {
        routedToAi:        pct(r.counts[CLASSIFICATIONS.ROUTED_TO_AI], r.total),
        routedToHuman:     pct(r.counts[CLASSIFICATIONS.ROUTED_TO_HUMAN], r.total),
        resolvedWithTools: pct(r.counts[CLASSIFICATIONS.RESOLVED_WITH_TOOLS], r.total),
        resolvedNoTools:   pct(r.counts[CLASSIFICATIONS.RESOLVED_NO_TOOLS], r.total),
        abandoned:         pct(r.counts[CLASSIFICATIONS.ABANDONED], r.total),
        failed:            pct(r.counts[CLASSIFICATIONS.FAILED], r.total),
      };
      return {
        key: r.key,
        day: r.day,
        name: r.name,
        role: r.role,
        roleConfidence: r.roleConfidence,
        sessions: r.total,
        resolvedTotal: r.resolvedTotal,
        successRate: r.successRate,
        pcts,
        uniqueCallers: extra.uniqueCallers ?? 0,
        types: extra.types ?? [],
        avgEvalScore: extra.avgEvalScore ?? null,
        avgMessages: extra.avgMessages ?? null,
        avgResponseMs: extra.avgResponseMs ?? null,
        avgDurationMs: extra.avgDurationMs ?? null,
      };
    })
    .sort((a, b) => (a.day === b.day ? b.sessions - a.sessions : b.day.localeCompare(a.day)));
}

function buildExtrasByDay(sessions) {
  const buckets = new Map();
  for (const s of sessions) {
    const name = s?.agentName ?? null;
    if (!name) continue;
    const day = isoDay(s.sessionStartTime);
    if (!day) continue;
    const key = `${day}|${name}`;
    if (!buckets.has(key)) {
      buckets.set(key, {
        key, day, name,
        callers: new Set(),
        types: new Set(),
        sumEval: 0, countEval: 0,
        sumMsgs: 0, countMsgs: 0,
        sumResp: 0, countResp: 0,
        sumDur: 0, countDur: 0,
      });
    }
    const b = buckets.get(key);
    if (s.ani) b.callers.add(s.ani);
    if (s.agentType) b.types.add(s.agentType);
    if (s.overallEvalScore != null)    { b.sumEval += s.overallEvalScore;    b.countEval += 1; }
    if (s.noOfTotalMessages != null)   { b.sumMsgs += s.noOfTotalMessages;   b.countMsgs += 1; }
    if (s.averageResponseTime != null) { b.sumResp += s.averageResponseTime; b.countResp += 1; }
    if (s.duration != null)            { b.sumDur  += s.duration;            b.countDur  += 1; }
  }
  return [...buckets.values()].map((b) => ({
    key: b.key, day: b.day, name: b.name,
    uniqueCallers: b.callers.size,
    types: [...b.types],
    avgEvalScore:  safeAvgFloat(b.sumEval,  b.countEval),
    avgMessages:   safeAvgFloat(b.sumMsgs,  b.countMsgs),
    avgResponseMs: safeAvgInt(b.sumResp,    b.countResp),
    avgDurationMs: safeAvgInt(b.sumDur,     b.countDur),
  }));
}

function isoDay(ts) {
  if (ts == null) return null;
  const n = typeof ts === 'number' ? ts : Number(ts);
  const d = !Number.isNaN(n) && n > 1e12 ? new Date(n) : new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}
function safeAvgFloat(sum, count) { return count > 0 ? sum / count : null; }
function safeAvgInt(sum, count) { return count > 0 ? Math.round(sum / count) : null; }
function pct(n, total) { return total > 0 ? Math.round((n / total) * 100) : 0; }

function RoleBadge({ role, confidence, resolvedTotal }) {
  const meta = ROLE_META[role] ?? ROLE_META.unknown;
  const lowConf = confidence != null && confidence < 0.3 && role !== 'unknown';
  const tooltip = `${meta.description}${
    resolvedTotal != null ? ` Based on ${resolvedTotal} resolved session${resolvedTotal === 1 ? '' : 's'} on this day.` : ''
  }${confidence != null ? ` Confidence: ${Math.round(confidence * 100)}%.` : ''}`;
  return (
    <Tooltip label={tooltip} multiline w={240}>
      <Badge size="sm" color={meta.color} variant={lowConf ? 'outline' : 'light'}>
        {meta.label}{lowConf ? '*' : ''}
      </Badge>
    </Tooltip>
  );
}
function RateBadge({ pct, color }) {
  if (pct == null) return <Text size="xs" c="dimmed">—</Text>;
  const variant = pct >= 20 ? 'filled' : 'light';
  return <Badge size="sm" color={color} variant={variant}>{pct}%</Badge>;
}
function EvalBadge({ score }) {
  if (score == null) return <Text size="xs" c="dimmed">—</Text>;
  const color = score < 0.5 ? 'red' : score < 0.7 ? 'orange' : score < 0.85 ? 'yellow' : 'teal';
  return <Badge size="sm" color={color} variant="light">{score.toFixed(2)}</Badge>;
}
